import { useCallback, useLayoutEffect, useState } from 'react';
import { useGameStore } from '../../store/gameSlice';
import { saveService } from '../../services/SaveService';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { DUNGEON_REGISTRY } from '../../data/dungeons.config';
import { DungeonDefinition } from '../../types/dungeon.types';
import { useUnlockedDungeonsStore } from '../../store/unlockedDungeonsSlice';
import { useDevtoolStore } from '../../store/devtoolSlice';
import { KeyDisplay } from './KeyDisplay';
import { netService } from '../../services/NetService';
import { multiplayerService } from '../../services/MultiplayerService';
import './DungeonMapScreen.scss';

// Native size of sprites/ui/dungeons.png. The whole screen is a fixed-size
// stage scaled to fit the viewport, so text and arrow stay aligned with the
// book art at any resolution.
const STAGE_W = 1138;
const STAGE_H = 378;

const ROWS_PER_COLUMN = 6;

// Zones expressed as fractions of the stage, measured from the book art
// (cf. docs/superpowers/specs/2026-06-13-dungeon-map-book-design.md). Tune
// these to nudge the list / arrow placement.
const LIST_ZONE = { left: 0.06, right: 0.45, top: 0.06, bottom: 0.87 };
const MAP_ZONE = { left: 0.5615, right: 0.9341, top: 0.045, bottom: 0.8598 };

const markerPercent = (marker: { x: number; y: number }) => ({
  left: `${(MAP_ZONE.left + marker.x * (MAP_ZONE.right - MAP_ZONE.left)) * 100}%`,
  top: `${(MAP_ZONE.top + marker.y * (MAP_ZONE.bottom - MAP_ZONE.top)) * 100}%`,
});

/**
 * See docs/dungeons.md and docs/game-design/run-loop.md ("Choix depuis le
 * hub"). The book art's left page lists unlocked dungeons; the right page is
 * a world map where an arrow points at the focused dungeon's `mapMarker`.
 */
export const DungeonMapScreen = () => {
  const setGameplay = useGameStore((s) => s.setGameplay);
  const setCurrentDungeonId = useGameStore((s) => s.setCurrentDungeonId);
  const isUnlocked = useUnlockedDungeonsStore((s) => s.isDungeonUnlocked);
  const unlockedIds = useUnlockedDungeonsStore((s) => s.unlocked);
  const devModeEnabled = useDevtoolStore((s) => s.devModeEnabled);
  const hasSave = saveService.hasSave();

  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const update = () =>
      setScale(
        Math.min(window.innerWidth / STAGE_W, window.innerHeight / STAGE_H),
      );
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const launchDungeon = useCallback(
    (dungeonId: string) => {
      if (hasSave) {
        return;
      }
      if (netService.isClient()) {
        return; // only the host picks the dungeon in co-op
      }
      if (netService.isHost()) {
        multiplayerService.launchDungeon(dungeonId);
        return;
      }
      saveService.clearRunSave();
      setCurrentDungeonId(dungeonId);
      setGameplay('dungeon');
    },
    [hasSave, setGameplay, setCurrentDungeonId],
  );

  void unlockedIds; // reactive dependency so the list refreshes when an unlock arrives

  const dungeons = Object.values(DUNGEON_REGISTRY).filter(
    (d) => isUnlocked(d.id) && (devModeEnabled || d.id !== 'test-arena'),
  );

  const columns: DungeonDefinition[][] = [];
  dungeons.forEach((def, i) => {
    const col = Math.floor(i / ROWS_PER_COLUMN);
    (columns[col] ??= []).push(def);
  });

  const { containerRef, focusedId } = useMenuNavigation({
    onConfirm: (id) => {
      if (id) {
        launchDungeon(id);
      }
    },
    onCancel: () => setGameplay('hub'),
  });

  const renderDifficulty = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);

  const marker = focusedId ? DUNGEON_REGISTRY[focusedId]?.mapMarker : undefined;

  return (
    <div className="dungeon-book-overlay">
      <div
        className="dungeon-book-stage"
        style={{
          width: STAGE_W,
          height: STAGE_H,
          transform: `scale(${scale})`,
        }}
      >
        <div
          className="dungeon-book-list"
          ref={containerRef}
          style={{
            left: `${LIST_ZONE.left * 100}%`,
            top: `${LIST_ZONE.top * 100}%`,
            width: `${(LIST_ZONE.right - LIST_ZONE.left) * 100}%`,
            height: `${(LIST_ZONE.bottom - LIST_ZONE.top) * 100}%`,
          }}
        >
          {columns.map((col, ci) => (
            <div className="dungeon-book-column" key={ci}>
              {col.map((def, ri) => (
                <div
                  className="dungeon-book-entry"
                  key={def.id}
                  data-nav-row={ri}
                  data-nav-col={ci}
                  data-nav-id={def.id}
                >
                  <div className="dungeon-book-entry-name">{def.label}</div>
                  <div className="dungeon-book-entry-stars">
                    {renderDifficulty(def.difficulty)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {marker && (
          <div className="dungeon-book-arrow" style={markerPercent(marker)}>
            <img src="/sprites/ui/map-arrow.svg" alt="" />
          </div>
        )}

        {hasSave && (
          <div
            className="dungeon-book-warning"
            style={{
              left: `${LIST_ZONE.left * 100}%`,
              width: `${(LIST_ZONE.right - LIST_ZONE.left) * 100}%`,
            }}
          >
            Une run est déjà en cours.
          </div>
        )}

        <div className="dungeon-book-hint">
          <KeyDisplay context="interface" action="CONFIRM" size="sm" /> lancer ·{' '}
          <KeyDisplay context="interface" action="CANCEL" size="sm" /> retour
        </div>
      </div>
    </div>
  );
};
